/**
 * Recurrence date math and descriptions for recurring job schedules
 * (tracker item 29). A port of backend recurring-jobs/date.engine.ts: every
 * date is a YYYY-MM-DD string and all arithmetic is done in UTC, so no local
 * time zone or DST shift can move a date by a day.
 *
 * The UI uses it to validate the start date against the pattern and to
 * describe a schedule; the mock client uses it to preview and generate.
 */

import { RecurringFrequency, RecurringFrequencyDetails } from '../api/types';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
/** Ordinals the backend accepts for "nth weekday" patterns (-1 = last). */
export const RECURRENCE_ORDINALS = [1, 2, 3, 4, -1] as const;
const ORDINAL_WORDS: Record<number, string> = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' };

/** True for a real calendar date written as YYYY-MM-DD. */
export function isDateOnly(value: string | null | undefined): value is string {
  if (!value || !DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function parse(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  const parsed = parse(date);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return format(parsed);
}

/** Jan 31 + 1 month = Feb 28/29 (clamped to month end), like the backend. */
export function addMonths(date: string, months: number): string {
  const parsed = parse(date);
  const day = parsed.getUTCDate();
  parsed.setUTCDate(1);
  parsed.setUTCMonth(parsed.getUTCMonth() + months);
  parsed.setUTCDate(Math.min(day, lastDayOfMonth(format(parsed))));
  return format(parsed);
}

/** 0 = Sunday */
export function dayOfWeek(date: string): number {
  return parse(date).getUTCDay();
}

export function dayOfMonth(date: string): number {
  return parse(date).getUTCDate();
}

export function lastDayOfMonth(date: string): number {
  const parsed = parse(date);
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0)).getUTCDate();
}

/** The nth (1-4, or -1 for last) `weekday` of `date`'s month, or null. */
export function nthWeekdayOfMonth(date: string, ordinal: number, weekday: number): string | null {
  if (!(RECURRENCE_ORDINALS as readonly number[]).includes(ordinal) || weekday < 0 || weekday > 6) return null;
  const parsed = parse(date);
  const year = parsed.getUTCFullYear();
  const month = parsed.getUTCMonth();
  if (ordinal === -1) {
    const last = new Date(Date.UTC(year, month + 1, 0));
    while (last.getUTCDay() !== weekday) last.setUTCDate(last.getUTCDate() - 1);
    return format(last);
  }
  const search = new Date(Date.UTC(year, month, 1));
  while (search.getUTCDay() !== weekday) search.setUTCDate(search.getUTCDate() + 1);
  search.setUTCDate(search.getUTCDate() + (ordinal - 1) * 7);
  return search.getUTCMonth() === month ? format(search) : null;
}

/** Backend calculateNextOccurrence. */
export function nextOccurrence(
  current: string,
  frequency: RecurringFrequency,
  interval: number,
  details?: RecurringFrequencyDetails | null
): string {
  switch (frequency) {
    case 'DAILY':
      return addDays(current, interval);
    case 'WEEKLY': {
      const days = (details?.daysOfWeek?.length ? details.daysOfWeek : [0]).slice().sort((a, b) => a - b);
      const today = dayOfWeek(current);
      const later = days.find((day) => day > today);
      if (later !== undefined) return addDays(current, later - today);
      return addDays(current, 7 - today + days[0] + (interval - 1) * 7);
    }
    case 'MONTHLY': {
      if (details?.ordinal !== undefined && details.ordinal !== null) {
        const target = addMonths(current, interval);
        return nthWeekdayOfMonth(target, details.ordinal, details.dayOfWeek || 0) ?? target;
      }
      const wanted = details?.dayOfMonth || dayOfMonth(current);
      if (dayOfMonth(current) < wanted && wanted <= lastDayOfMonth(current)) {
        return `${current.slice(0, 8)}${String(wanted).padStart(2, '0')}`;
      }
      const target = addMonths(current, interval);
      return `${target.slice(0, 8)}${String(Math.min(wanted, lastDayOfMonth(target))).padStart(2, '0')}`;
    }
    case 'QUARTERLY':
      return addMonths(current, 3 * interval);
    case 'ANNUALLY':
      return addMonths(current, 12 * interval);
    default:
      throw new Error(`Unknown frequency: ${frequency}`);
  }
}

/**
 * Backend generateOccurrences: from `start` (listed itself, except that a
 * MONTHLY nth-weekday pattern snaps forward to its first match) up to
 * `until` inclusive, at most `limit` dates.
 */
export function listOccurrences(
  start: string,
  frequency: RecurringFrequency,
  interval: number,
  details: RecurringFrequencyDetails | null | undefined,
  until: string | null | undefined,
  limit: number
): string[] {
  const dates: string[] = [];
  let current = start;
  if (frequency === 'MONTHLY' && details?.ordinal !== undefined && details.ordinal !== null) {
    const first = nthWeekdayOfMonth(start, details.ordinal, details.dayOfWeek || 0);
    current = first && first >= start ? first : nextOccurrence(start, frequency, interval, details);
  }
  for (let guard = 0; dates.length < limit && guard < limit * 10; guard += 1) {
    if (until && current > until) break;
    dates.push(current);
    current = nextOccurrence(current, frequency, interval, details);
  }
  return dates;
}

/** Backend validateFrequencyConfig: an error message, or null when valid. */
export function frequencyConfigError(
  frequency: RecurringFrequency,
  interval: number,
  details?: RecurringFrequencyDetails | null
): string | null {
  if (!Number.isInteger(interval) || interval < 1) return 'Repeat every must be a whole number of 1 or more.';
  switch (frequency) {
    case 'WEEKLY':
      if (!details?.daysOfWeek || details.daysOfWeek.length === 0) return 'Choose at least one day of the week.';
      if (details.daysOfWeek.some((day) => !Number.isInteger(day) || day < 0 || day > 6)) return 'Days of the week must be Sunday to Saturday.';
      return null;
    case 'MONTHLY':
      if (details?.ordinal !== undefined) {
        if (!(RECURRENCE_ORDINALS as readonly number[]).includes(details.ordinal)) return 'Choose first, second, third, fourth or last.';
        if (details.dayOfWeek === undefined || details.dayOfWeek < 0 || details.dayOfWeek > 6) return 'Choose a day of the week.';
      } else if (details?.dayOfMonth !== undefined) {
        if (!Number.isInteger(details.dayOfMonth) || details.dayOfMonth < 1 || details.dayOfMonth > 31) return 'Day of the month must be 1 to 31.';
      }
      return null;
    case 'DAILY':
    case 'QUARTERLY':
    case 'ANNUALLY':
      return null;
    default:
      return `Unknown frequency: ${frequency}`;
  }
}

export function ordinalSuffix(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return 'th';
  switch (value % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function every(interval: number, singular: string, plural: string): string {
  return interval > 1 ? `Every ${interval} ${plural}` : singular;
}

/**
 * Human-readable pattern, e.g. "Weekly on Mon, Thu", "Every 2 months on the
 * last Friday", "Monthly on the 15th". Unlike the server's
 * getRecurrenceDescription it includes the interval for every frequency and
 * never prints "the lastth" when no details are stored.
 */
export function describeRecurrence(
  frequency: string,
  interval: number | null | undefined,
  details?: RecurringFrequencyDetails | null
): string {
  const n = interval && interval > 0 ? interval : 1;
  switch (frequency) {
    case 'DAILY':
      return every(n, 'Daily', 'days');
    case 'WEEKLY': {
      const days = (details?.daysOfWeek?.length ? details.daysOfWeek : [0])
        .slice()
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_SHORT[day] ?? `day ${day}`)
        .join(', ');
      return `${every(n, 'Weekly', 'weeks')} on ${days}`;
    }
    case 'MONTHLY': {
      const base = every(n, 'Monthly', 'months');
      if (details?.ordinal !== undefined && details.ordinal !== null) {
        const ordinal = ORDINAL_WORDS[details.ordinal] ?? `#${details.ordinal}`;
        return `${base} on the ${ordinal} ${WEEKDAY_NAMES[details.dayOfWeek ?? 0] ?? 'day'}`;
      }
      if (details?.dayOfMonth) return `${base} on the ${details.dayOfMonth}${ordinalSuffix(details.dayOfMonth)}`;
      return `${base} on the start date's day of the month`;
    }
    case 'QUARTERLY':
      return n > 1 ? `Every ${n * 3} months` : 'Quarterly';
    case 'ANNUALLY':
      return every(n, 'Annually', 'years');
    default:
      return 'Unknown pattern';
  }
}
